import {
  Classes,
  PopoverNext,
  type PopoverProps,
  popoverPropsToNextProps,
} from "@blueprintjs/core";
import { ThemeType } from "@hex/common";
import classNames from "classnames";
import { lighten } from "polished";
import React from "react";
import styled, { css } from "styled-components";

import { inputStyles } from "./HexInput";
import { menuStyles } from "./HexMenuItem";

export const POPOVER_POINTER_EVENTS_NONE = "popover-pointer-events-none";

export type HexPopoverProps = Omit<PopoverProps, "children"> & {
  /**
   * Popovers only handle one child, additional children are ignored in BP's implementation. This fixes the incorrect typing in the BP Popover component.
   */
  children?: React.ReactChild | null;
  dataTest?: string;
  scrollContent?: boolean;
};

type HexPortalPopoverProps = Omit<PopoverProps, "children"> & {
  /**
   * Popovers only handle one child, additional children are ignored in BP's implementation. This fixes the incorrect typing in the BP Popover component.
   */
  children?: React.ReactChild | null;
  realClassName?: string;
  dataTest?: string;
};

/**
 * This component is an intermediate component that allows us to use
 * styled components with a class prop that isn't `className`.
 *
 * We basically just save off the consumer `className` to `realClassName`, applying the
 * styles added by styled components to `popoverClassName`.
 */
const HexPortalPopover: React.ComponentType<HexPortalPopoverProps> = React.memo(
  function HexPortalPopover({
    className,
    dataTest,
    popoverClassName,
    realClassName,
    ...props
  }) {
    return (
      <PopoverNext
        {...popoverPropsToNextProps(props)}
        className={realClassName}
        data-test={dataTest}
        // make sure we don't lose the `popoverClassName` added by the consumer
        popoverClassName={classNames(className, popoverClassName)}
      />
    );
  },
);

export const popoverStyles = css<{ $scrollContent?: boolean }>`
  transform: scale(1) !important;
  border-radius: ${({ theme }) => theme.borderRadius};
  box-shadow: ${({ theme }) => theme.boxShadow.POPOVER};

  .${Classes.POPOVER_CONTENT} {
    background: ${({ theme }) => theme.backgroundColor.DEFAULT};
  }

  .${Classes.POPOVER_ARROW} {
    &::before {
      box-shadow: none;
    }

    .${Classes.POPOVER_ARROW + "-border"} {
      ${({ theme }) =>
        theme.type === ThemeType.DARK
          ? css`
              fill: ${lighten(0.04, theme.backgroundColor.DEFAULT)};
              fill-opacity: 1;
            `
          : css`
              fill: ${theme.borderColor.DEFAULT};
              fill-opacity: 0.5;
            `}
    }

    .${Classes.POPOVER_ARROW + "-fill"} {
      fill: ${({ theme }) => theme.backgroundColor.DEFAULT};
    }
  }

  .${Classes.INPUT_GROUP} {
    input {
      ${inputStyles}
    }
  }

  .${Classes.MENU} {
    ${menuStyles}
    background-color: transparent;
    overflow-x: hidden;
  }
`;

const StyledHexPortalPopover = styled(HexPortalPopover)<{
  $scrollContent?: boolean;
}>`
  && {
    ${popoverStyles}
  }
`;

export const HexPopover: React.ComponentType<HexPopoverProps> = React.memo(
  function HexPopover({ className, dataTest, scrollContent, ...props }) {
    return (
      <StyledHexPortalPopover
        {...props}
        $scrollContent={scrollContent}
        data-test={dataTest}
        realClassName={className}
      />
    );
  },
);

/**
 * Passes className in to popoverClassName so that styled-components styles
 * will apply to the popover rather than the target.
 */
export const StyleableHexPopover: React.ComponentType<HexPopoverProps> = ({
  className,
  popoverClassName,
  ...props
}) => (
  <HexPopover
    {...props}
    popoverClassName={classNames(className, popoverClassName)}
  />
);
